import { css, cva } from '@styled-system/css';
import { ArrowRight, Folder as FolderIcon, Search, User } from 'lucide-react';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useVestiaireLocked } from '@/features/subscription/hooks/useVestiaireAccess';
import { creationPlatform } from '@/features/subscription/vestiaireAccess';
import { isPlausibleVestiaireSellerInput, vestiaireNameFromInput } from '@/features/vendorSearch/vestiaireSellerRef';
import type { AnalysisPlatform } from '@/shared/api/types/generated';
import { PlatformLogo } from '@/shared/components/analysis/PlatformLogo';
import { VestiaireLockedChip } from '@/shared/components/analysis/VestiaireLockedChip';
import { Button } from '@/shared/components/ui/Button';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { FormField } from '@/shared/components/ui/FormField';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useGetSellerByLoginQuery, useParseVestiaireSellerRefQuery } from '@/shared/hooks/queries/generated';

// ============================================================================
// Barre de recherche vendeur « expanding » — pas d'overlay : la barre se
// déplie vers le bas en panneau attaché (pattern command palette). On colle
// l'URL du profil directement dans la barre, le panneau révèle nom + dossier
// et le bouton de lancement. Clic extérieur / Échap replie.
// ============================================================================

const wrap = cva({
  base: {
    position: 'relative',
    width: '100%',
    maxWidth: '720px',
  },
  variants: {
    // Ouvert : la barre + son panneau passent AU-DESSUS du contenu en dessous
    expanded: {
      true: { zIndex: 50 },
      false: {},
    },
  },
});

const container = cva({
  base: {
    width: '100%',
    bg: 'surface.elevated',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.default',
    borderRadius: 'xl',
    boxShadow: 'lg',
    transition: 'border-color 120ms ease, box-shadow 160ms ease, transform 160ms ease',
  },
  variants: {
    expanded: {
      true: {
        borderColor: 'border.strong',
        boxShadow: 'xl',
        // Le panneau se colle dessous : on aplatit la jonction
        borderBottomLeftRadius: '0',
        borderBottomRightRadius: '0',
      },
      false: {
        cursor: 'pointer',
        _hover: {
          borderColor: 'border.strong',
          boxShadow: 'xl',
          transform: 'translateY(-1px)',
        },
      },
    },
  },
});

// 58px + les 2 bordures du container = 60px total, comme la barre produit
const topRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  paddingLeft: '7',
  paddingRight: '5',
  height: '58px',
  color: 'text.tertiary',
});

const urlInput = css({
  flex: 1,
  minWidth: 0,
  height: '100%',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontFamily: 'sans',
  fontSize: 'body',
  color: 'text.primary',
  _placeholder: { color: 'text.tertiary' },
});

// Panneau déplié — OVERLAY absolu collé sous la barre (le contenu de la page
// ne bouge pas), même carte visuellement : bords joints, filet de séparation.
const panel = css({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 50,
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.strong',
  borderTopWidth: '1px',
  borderTopColor: 'border.subtle',
  borderBottomLeftRadius: 'xl',
  borderBottomRightRadius: 'xl',
  boxShadow: 'xl',
  paddingX: '7',
  paddingY: '6',
  display: 'flex',
  flexDirection: 'column',
  gap: '5',
  textAlign: 'left',
});

const platformRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
});

const fieldsRow = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '5',
  '@media (max-width: 640px)': { gridTemplateColumns: '1fr' },
});

const footerRow = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '4',
});

const urlErrorText = css({
  fontSize: 'caption',
  color: 'error.default',
});

/** URL de profil Vinted valide : vinted.xx/member/<id>-<pseudo> */
const isVintedMemberUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return /^(www\.)?vinted\.\w+(\.\w+)?$/.test(parsed.hostname) && /\/member\/\d+/.test(parsed.pathname);
  } catch {
    return false;
  }
};

/** Pseudo Vinted plausible (pas une URL) : lettres/chiffres/._- , 3+ car. */
const isVintedUsername = (value: string): boolean => !value.includes('/') && /^[\w.-]{3,}$/.test(value);

/** Déduit le pseudo depuis l'URL (…/member/123-pseudo → pseudo) ou l'entrée. */
const nameFromInput = (value: string): string => {
  const match = /\/member\/\d+-([\w.-]+)/.exec(value);
  if (match) return match[1];
  return isVintedUsername(value) ? value : '';
};

/**
 * Résout l'entrée en URL de profil PORTEUSE DE L'ID NUMÉRIQUE (la création
 * exige sellerVintedId, jamais un pseudo). URL membre → telle quelle ; id
 * numérique → URL membre ; pseudo (@ accepté) → résolution getSellerByLogin
 * EN BASE RECOP — un vendeur jamais croisé par l'ingester est introuvable
 * même s'il existe sur Vinted (repli : coller l'URL du profil).
 */
const resolveProfileUrl = async (value: string): Promise<{ url: string } | { error: 'invalid' | 'unknown' }> => {
  if (value.includes('/')) {
    return isVintedMemberUrl(value) ? { url: value } : { error: 'invalid' };
  }
  const raw = value.replace(/^@/, '').trim();
  if (/^\d+$/.test(raw)) return { url: `https://www.vinted.fr/member/${raw}` };
  if (!isVintedUsername(raw)) return { error: 'invalid' };
  const result = await useGetSellerByLoginQuery.fetcher({ login: raw })();
  if (!result.getSellerByLogin) return { error: 'unknown' };
  return { url: `https://www.vinted.fr/member/${result.getSellerByLogin.sellerVintedId}` };
};

export interface VendorSearchBarInput {
  title: string;
  url: string;
  folderId?: string | null;
  platform: AnalysisPlatform;
  /** Id du vendeur VC (rendu par parseVestiaireSellerRef), absent sur Vinted. */
  sellerId?: string;
}

export interface VendorSearchBarProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: VendorSearchBarInput) => Promise<void>;
  /** Pré-affecte l'analyse créée à ce dossier (bouton « + » d'un dossier). */
  initialFolderId?: string | null;
  /** Dossiers serveur pour le sélecteur (fournis par la page). */
  folders?: { id: string; name: string; position: number }[];
}

export const VendorSearchBar: FC<VendorSearchBarProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  initialFolderId = null,
  folders = [],
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  const [url, setUrl] = useState('');
  const [nom, setNom] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ url?: string; nom?: string }>({});
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState<AnalysisPlatform>('vinted');
  const isVestiaire = platform === 'vestiaire';
  // Recherche VC réservée aux plans qui l'incluent : puce verrouillée, et onglet VC choisi avant
  // l'arrivée de la réponse (accès refusé) ⇒ retour à Vinted.
  const vestiaireLocked = useVestiaireLocked();
  useEffect(() => {
    const allowed = creationPlatform(platform, vestiaireLocked);
    if (allowed !== platform) setPlatform(allowed);
  }, [platform, vestiaireLocked]);

  const folderOptions = useMemo(
    () => [
      { label: t('vendorSearch.noFolder'), value: '__none__' },
      ...[...folders].sort((a, b) => a.position - b.position).map((f) => ({ label: f.name, value: f.id })),
    ],
    [folders, t],
  );

  // Ouverture : focus la barre, applique le dossier pré-affecté ; le reste de
  // la saisie est conservé (un clic hors du panneau ne perd rien).
  useEffect(() => {
    if (isOpen) {
      setFolderId(initialFolderId ?? null);
      urlInputRef.current?.focus();
    }
  }, [isOpen, initialFolderId]);

  // Clic extérieur + Échap → replie
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onOpenChange]);

  // Le nom se pré-remplit depuis le pseudo de l'URL tant qu'il n'a pas été
  // édité à la main.
  const handleUrlChange = (value: string) => {
    setUrl(value);
    if (errors.url) setErrors((e) => ({ ...e, url: undefined }));
    if (!nameTouched) setNom(isVestiaire ? vestiaireNameFromInput(value) : nameFromInput(value));
  };

  // Changement de plateforme : l'erreur d'URL ne s'applique plus (chaque
  // plateforme a ses propres règles), mais le texte tapé est conservé.
  const handlePlatformChange = (next: AnalysisPlatform) => {
    setPlatform(next);
    if (errors.url) setErrors((e) => ({ ...e, url: undefined }));
  };

  // Échec de création (409 « analyse déjà en cours », réseau…) : message sous la barre, saisie
  // conservée — jamais un rejet non géré (handleSubmit est appelé en `void`).
  const submitOrShowError = async (input: VendorSearchBarInput): Promise<boolean> => {
    try {
      await onSubmit(input);
      return true;
    } catch (error) {
      setErrors({ url: error instanceof Error && error.message ? error.message : t('productSearch.createError') });
      return false;
    }
  };

  const handleSubmit = async () => {
    const raw = url.trim();

    if (isVestiaire) {
      if (!isPlausibleVestiaireSellerInput(raw)) {
        setErrors({ url: t('vendorSearch.urlErrorVestiaire') });
        return;
      }
      setErrors({});
      setLoading(true);
      try {
        let ref;
        try {
          ({ parseVestiaireSellerRef: ref } = await useParseVestiaireSellerRefQuery.fetcher({ input: raw })());
        } catch {
          // Erreur réseau : même message générique que Vinted (productSearch.createError).
          setErrors({ url: t('productSearch.createError') });
          return;
        }
        // Vendeur introuvable ou jamais croisé par Recop (l'analyse serait vide et coûterait un
        // crédit, D4-4) : un seul message, comme Vinted ne distingue pas ces deux cas non plus.
        if (!ref || !ref.known) {
          setErrors({ url: t('vendorSearch.vestiaireUnknown') });
          return;
        }
        // Nom validé APRÈS résolution sur VC : on complète par le pseudo serveur avant d'exiger un nom.
        const resolvedName = nom.trim() || ref.username?.trim() || '';
        if (!resolvedName) {
          setErrors({ nom: t('vendorSearch.nameError') });
          return;
        }
        if (
          !(await submitOrShowError({
            title: resolvedName,
            url: ref.profileUrl,
            sellerId: ref.sellerId,
            folderId,
            platform,
          }))
        ) {
          return;
        }
        setUrl('');
        setNom('');
        setNameTouched(false);
        setFolderId(null);
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Chemin Vinted existant, inchangé.
    const next: { url?: string; nom?: string } = {};
    if (!raw) next.url = t('vendorSearch.urlError');
    if (!nom.trim()) next.nom = t('vendorSearch.nameError');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    try {
      const resolved = await resolveProfileUrl(raw);
      if ('error' in resolved) {
        setErrors({
          url: resolved.error === 'unknown' ? t('vendorSearch.unknownVendor') : t('vendorSearch.urlError'),
        });
        return;
      }
      if (!(await submitOrShowError({ title: nom.trim(), url: resolved.url, folderId, platform }))) return;
      setUrl('');
      setNom('');
      setNameTouched(false);
      setFolderId(null);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={wrap({ expanded: isOpen })} ref={containerRef}>
      <div
        className={container({ expanded: isOpen })}
        onClick={isOpen ? undefined : () => onOpenChange(true)}
        role={isOpen ? undefined : 'button'}
        tabIndex={isOpen ? undefined : 0}
        onKeyDown={
          isOpen
            ? undefined
            : (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpenChange(true);
                }
              }
        }
      >
        {/* ─── Barre (toujours visible) ──────────────────────────────── */}
        <div className={topRow}>
          <Search size={18} strokeWidth={1.75} />
          <input
            ref={urlInputRef}
            className={urlInput}
            placeholder={t('vendorSearch.searchPlaceholder')}
            value={url}
            onFocus={() => onOpenChange(true)}
            onChange={(event) => handleUrlChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
              }
            }}
          />
          {!isOpen && (
            <Button variant="primary" size="md" trailingIcon={<ArrowRight size={14} strokeWidth={1.75} />}>
              {t('vendorSearch.newSearch')}
            </Button>
          )}
        </div>

        {/* ─── Panneau déplié : critères + lancement ─────────────────── */}
        {isOpen && (
          <div className={panel}>
            <div className={platformRow}>
              <ChipToggle
                selected={!isVestiaire}
                onChange={() => handlePlatformChange('vinted')}
                icon={<PlatformLogo platform="vinted" />}
              >
                {t('vestiaireSearch.tabVinted')}
              </ChipToggle>
              {vestiaireLocked ? (
                <VestiaireLockedChip />
              ) : (
                <ChipToggle
                  selected={isVestiaire}
                  onChange={() => handlePlatformChange('vestiaire')}
                  icon={<PlatformLogo platform="vestiaire" />}
                >
                  {t('vestiaireSearch.tabVestiaire')}
                </ChipToggle>
              )}
            </div>

            {errors.url && <span className={urlErrorText}>{errors.url}</span>}

            <div className={fieldsRow}>
              <FormField
                label={t('vendorSearch.nameLabel')}
                icon={<User size={14} strokeWidth={1.75} />}
                hint={errors.nom}
                hintTone={errors.nom ? 'error' : 'neutral'}
              >
                <Input
                  placeholder={t('vendorSearch.namePlaceholder')}
                  value={nom}
                  onChange={(event) => {
                    setNom(event.target.value);
                    setNameTouched(true);
                    if (errors.nom) setErrors((e) => ({ ...e, nom: undefined }));
                  }}
                  state={errors.nom ? 'error' : 'default'}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleSubmit();
                    }
                  }}
                />
              </FormField>

              <FormField label={t('vendorSearch.folderLabel')} icon={<FolderIcon size={14} strokeWidth={1.75} />}>
                <Select
                  value={folderId ?? '__none__'}
                  onChange={(value) => setFolderId(value === '__none__' ? null : value)}
                  options={folderOptions}
                  placeholder={t('vendorSearch.noFolder')}
                />
              </FormField>
            </div>

            <div className={footerRow}>
              <Button variant="ghost" size="md" onClick={() => onOpenChange(false)} disabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                size="md"
                trailingIcon={<ArrowRight size={14} strokeWidth={1.75} />}
                handleClick={handleSubmit}
                showLoader
              >
                {t('vendorSearch.submit')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
